import { VALIDATION_CONSTANTS } from "../constants";

/**
 * Validation utility functions
 */

export const validateEmail = (email: string): boolean => {
  if (!email || email.trim() === '') return false;
  // Check for consecutive dots
  if (email.includes('..')) return false;
  return VALIDATION_CONSTANTS.EMAIL_REGEX.test(email);
};

export const validateTelephone = (telephone: string): boolean => {
  if (!telephone || telephone.trim() === '') return false;
  const cleaned = telephone.replace(/\s/g, '');
  // Check if it contains only digits and valid phone characters
  if (!/^[\+]?[\d\-\(\)]+$/.test(cleaned)) return false;
  // Check length (UK phone numbers are typically 10-15 digits)
  const digitsOnly = cleaned.replace(/[\+\-\(\)]/g, '');
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

export const validateLoanAmount = (amount: number): boolean => {
  return amount >= VALIDATION_CONSTANTS.MIN_LOAN_AMOUNT;
};

export const validatePositiveNumber = (value: number): boolean => {
  return value > 0;
};

export const validatePercentage = (value: number): boolean => {
  return value >= 0 && value <= 100;
};

export const validateRequired = (value: string | number): boolean => {
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return value > 0;
};

export const getValidationMessage = (field: string, value: string): string => {
  const messages: Record<string, Record<string, string>> = {
    email: {
      required: "Email address is required",
      invalid: "Please enter a valid email address"
    },
    telephone: {
      required: "Telephone number is required", 
      invalid: "Please enter a valid UK phone number"
    },
    propertyValue: {
      required: "Property value is required",
      invalid: "Property value must be greater than 0"
    },
    loanRequired: {
      required: "Loan amount is required",
      invalid: `Minimum loan amount is £${VALIDATION_CONSTANTS.MIN_LOAN_AMOUNT / 100}`
    },
    loanTerm: {
      required: "Loan term is required",
      invalid: "Loan term must be greater than 0"
    },
    payRate: {
      required: "Pay rate is required",
      invalid: "Pay rate must be greater than 0"
    }
  };

  // If no value provided, return required message
  if (!value || value.trim() === '') {
    return messages[field]?.required || '';
  }

  // Validate based on field type
  if (field === 'email') {
    return validateEmail(value) ? '' : messages.email.invalid;
  }
  
  if (field === 'telephone') {
    return validateTelephone(value) ? '' : messages.telephone.invalid;
  }

  // For unknown fields, return empty string
  return '';
};
